import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  Pagination,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Alert,
  AlertTitle,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import { CircularProgress } from "@mui/material";
import {
  History,
  CalendarToday,
  LocalHospital,
  ExpandMore,
  Science,
  Biotech,
  Assignment,
  WarningAmberOutlined,
} from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { DESIGN_TOKENS } from "../../../design-system/tokens/colors";
import { apiClient } from "../../../services/api";
import { API_CONFIG } from "../../../config/api";
import { storageService } from "../../../services/storageService";

export interface ExamData {
  _key: string;
  _id: string;
  cattle_tag: string;
  handling_details: {
    exam_type: string;
    exam_result?: string;
    plate_id?: string;
    collection_date?: string;
    exam_date?: string;
    attachments?: Array<{
      type: string;
      url: string;
    }> | string | { type?: string; url?: string };
    results?: {
      staphPrimary?: string;
      staphSecondary?: string;
      strepPrimary?: string;
      strepSecondary?: string;
      generalPrimary?: string;
      generalSecondary?: string;
      milkAspect?: string[];
      udderSigns?: string[];
      teatsAffected?: string[];
      observation?: string;
      result?: string;
      exam_result?: string;
      // Novos campos de tratamento
      situation?: string;
      mainTreatment?: string;
      supportiveTreatment?: string;
      recommendedAntibiotics?: string;
      treatmentPlan?: any;
      aines?: string[];
    };
    [key: string]: any;
  };
}

export interface ExamDetailProps {
  examData: {
    exams?: ExamData[];
    totalExams?: number;
    resumo?: string;
  } | ExamData[];
}

const ExamDetail: React.FC<ExamDetailProps> = ({ examData }) => {
  // Estados para paginação
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Estado do modal de imagem
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [imageModalSrc, setImageModalSrc] = useState<string | null>(null);
  const [tempAccessLoadingKey, setTempAccessLoadingKey] = useState<string | null>(null);
  const [tempAccessError, setTempAccessError] = useState<string | null>(null);
  const [tempAccessErrorKey, setTempAccessErrorKey] = useState<string | null>(null);

  const handleOpenImageModal = (url: string) => {
    setImageModalSrc(url);
    setImageModalOpen(true);
  };

  const handleCloseImageModal = () => {
    setImageModalOpen(false);
    setImageModalSrc(null);
  };

  const fetchTempAccessAndOpen = async (objectKey: string) => {
    try {
      setTempAccessLoadingKey(objectKey);
      setTempAccessError(null);
      setTempAccessErrorKey(null);
      const resp = await storageService.getTempAccess({
        bucket: (import.meta as any)?.env?.VITE_S3_BUCKET_EXAMES || "exames",
        objectKey,
        request: {
          expiresInMinutes: 300,
          maxAccess: 100,
          description: "Preview",
        },
      });
      if (resp?.accessUrl) {
        handleOpenImageModal(resp.accessUrl);
      } else {
        setTempAccessError("Não foi possível obter acesso temporário para a imagem.");
        setTempAccessErrorKey(objectKey);
      }
    } catch (e) {
      setTempAccessError("Erro ao gerar acesso temporário. Tente novamente.");
      setTempAccessErrorKey(objectKey);
    } finally {
      setTempAccessLoadingKey(null);
    }
  };

  // Extrair array de exames do formato correto
  const examsArray: ExamData[] = useMemo(() => {
    if (!examData) return [];
    
    // Se examData é um array, retorna diretamente
    if (Array.isArray(examData)) {
      return examData;
    }
    
    // Se examData é um objeto com propriedade exams, retorna exams
    if (examData && typeof examData === 'object' && 'exams' in examData) {
      return examData.exams || [];
    }
    
    return [];
  }, [examData]);

  // Helper: normaliza attachments e retorna primeira URL de imagem
  const getFirstImageAttachmentUrl = (attachments: any): string | null => {
    try {
      let arr: any[] = [];
      if (Array.isArray(attachments)) {
        arr = attachments;
      } else if (typeof attachments === 'string') {
        const parsed = JSON.parse(attachments);
        if (Array.isArray(parsed)) arr = parsed;
        else if (parsed && typeof parsed === 'object') arr = [parsed];
      } else if (attachments && typeof attachments === 'object') {
        arr = [attachments];
      }
      const att = arr.find((a) => a && a.type === 'image' && a.url);
      return att?.url ? String(att.url).replace(/[`]/g, '').trim() : null;
    } catch {
      return null;
    }
  };

  // Componente de imagem autenticada
  const ExamImage: React.FC<{ src: string; alt?: string }> = ({ src, alt }) => {
    const [resolvedSrc, setResolvedSrc] = useState<string>(src);
    const [errored, setErrored] = useState<boolean>(false);

    useEffect(() => {
      let objectUrl: string | null = null;
      let cancelled = false;

      const load = async () => {
        try {
          const base = API_CONFIG.baseURL || "";
          let endpoint: string | null = null;
          if (base && src.startsWith(base)) {
            endpoint = src.substring(base.length);
          } else if (src.startsWith("/")) {
            endpoint = src; // relativo ao base da API
          }

          if (endpoint) {
            const blob = await apiClient.getBlob(endpoint);
            if (cancelled) return;
            objectUrl = URL.createObjectURL(blob);
            setResolvedSrc(objectUrl);
          } else {
            setResolvedSrc(src);
          }
        } catch (e) {
          setErrored(true);
        }
      };

      load();
      return () => {
        cancelled = true;
        if (objectUrl) {
          URL.revokeObjectURL(objectUrl);
        }
      };
    }, [src]);

    if (errored) {
      return (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Não foi possível carregar a imagem.
          </Typography>
        </Box>
      );
    }

    return (
      <Box
        component="img"
        src={resolvedSrc}
        alt={alt || "Imagem do exame"}
        sx={{
          width: "100%",
          maxWidth: 640,
          height: "auto",
          borderRadius: 1,
          border: "1px solid",
          borderColor: DESIGN_TOKENS.colors.neutral[200],
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        }}
        onError={() => setErrored(true)}
      />
    );
  };

  // Função para determinar gravidade do resultado clínico
  const getResultSeverity = (result: string): 'grave' | 'medio' | 'leve' => {
    if (!result) return 'leve';
    
    const resultLower = result.toLowerCase();
    
    // Resultados graves (vermelho)
    if (resultLower.includes('staphylococcus aureus') || 
        resultLower.includes('positivo') ||
        resultLower.includes('grave') ||
        resultLower.includes('severo') ||
        resultLower.includes('crítico') ||
        resultLower.includes('alto') ||
        resultLower.includes('elevado') ||
        resultLower.includes('infecção') ||
        resultLower.includes('infeccao') ||
        resultLower.includes('patogênico') ||
        resultLower.includes('patogenico')) {
      return 'grave';
    }
    
    // Resultados médios (laranja)
    if (resultLower.includes('moderado') ||
        resultLower.includes('médio') ||
        resultLower.includes('medio') ||
        resultLower.includes('atenção') ||
        resultLower.includes('atencao') ||
        resultLower.includes('suspeito') ||
        resultLower.includes('intermediário') ||
        resultLower.includes('intermediario') ||
        resultLower.includes('parcial') ||
        resultLower.includes('limitrofe')) {
      return 'medio';
    }
    
    return 'leve';
  };

  // Função para obter cor baseada na gravidade do resultado
  const getSeverityColor = (severity: 'grave' | 'medio' | 'leve'): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    return 'default';
  };

  // Função para obter ícone do tipo de exame
  const getExamIcon = (examType: string) => {
    switch (examType?.toLowerCase()) {
      case 'mastitis_test':
      case 'teste_mastite':
        return <LocalHospital />;
      case 'microbiological':
      case 'microbiologico':
        return <Biotech />;
      default:
        return <Science />;
    }
  };

  // Função para obter cor do tipo de exame
  const getExamColor = (examType: string) => {
    switch (examType?.toLowerCase()) {
      case 'mastitis_test':
      case 'teste_mastite':
        return 'grey.600';
      case 'microbiological':
      case 'microbiologico':
        return 'primary.main';
      default:
        return 'info.main';
    }
  };

  // Função para traduzir tipo de exame
  const translateExamType = (examType: string): string => {
    switch (examType?.toLowerCase()) {
      case 'mastitis_test':
        return 'Teste de Mastite';
      case 'microbiological':
        return 'Exame Microbiológico';
      default:
        return examType?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Exame Laboratorial';
    }
  };

  // Processar e ordenar exames (mais recente primeiro)
  const sortedExams = useMemo(() => {
    return [...examsArray].sort((a, b) => {
      const dateA = new Date(a.handling_details?.exam_date || a.handling_details?.collection_date || '');
      const dateB = new Date(b.handling_details?.exam_date || b.handling_details?.collection_date || '');
      return dateB.getTime() - dateA.getTime();
    });
  }, [examsArray]);

  // Calcular paginação
  const totalPages = Math.ceil(sortedExams.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = sortedExams.slice(startIndex, startIndex + itemsPerPage);

  return (
    <Box sx={{ p: 3 }}>
      {/* Modal para visualizar a imagem do exame */}
      <Dialog
        open={imageModalOpen}
        onClose={handleCloseImageModal}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { p: 2 } }}
      >
        <DialogTitle>Placa de teste</DialogTitle>
        <DialogContent>
          {imageModalSrc ? (
            <ExamImage src={imageModalSrc} alt="Placa de teste" />
          ) : (
            <Typography variant="body2" color="text.secondary">
              Nenhuma imagem disponível.
            </Typography>
          )}
        </DialogContent>
      </Dialog>
      {/* Histórico de Exames - Timeline Vertical com Paginação */}
      <Card sx={{ border: '1px solid', borderColor: 'grey.200' }}>
        <CardContent>
          {/* Header com título e resumo */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <History color="primary" />
              Histórico de Exames Laboratoriais
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {sortedExams.length} exame(s) encontrado(s)
            </Typography>
          </Box>

          {/* Timeline Vertical */}
          {sortedExams.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                Nenhum exame encontrado
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ position: 'relative' }}>
                {currentItems.map((exam, index) => (
                  <Box key={`${exam._key}-${index}`} sx={{ display: 'flex', mb: 3, position: 'relative' }}>
                    {/* Timeline Dot */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mr: 3, minWidth: 40 }}>
                      <Avatar
                        sx={{ 
                          bgcolor: getExamColor(exam.handling_details?.exam_type || ''),
                          width: 40,
                          height: 40,
                          fontSize: 20
                        }}
                      >
                        {getExamIcon(exam.handling_details?.exam_type || '')}
                      </Avatar>
                      {index < currentItems.length - 1 && (
                        <Box 
                          sx={{ 
                            width: 2, 
                            height: 60, 
                            bgcolor: 'grey.300',
                            mt: 1,
                          }} 
                        />
                      )}
                    </Box>
                    
                    {/* Timeline Content */}
                    <Box sx={{ flex: 1, pt: 0.5 }}>
                      <Paper 
                        elevation={1} 
                        sx={{ 
                          p: 3, 
                          bgcolor: 'background.paper',
                          border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`,
                          borderRadius: 2,
                          '&:hover': {
                            boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                            transform: 'translateY(-1px)',
                            transition: 'all 0.2s ease-in-out'
                          }
                        }}
                      >
                        <Box sx={{ mb: 2 }}>
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              fontWeight: 600, 
                              color: 'text.primary',
                              mb: 1
                            }}
                          >
                            {translateExamType(exam.handling_details?.exam_type || '')}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Assignment sx={{ fontSize: 16, color: 'text.secondary' }} />
                              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                Animal: {exam.cattle_tag}
                              </Typography>
                            </Box>
                            {(exam.handling_details?.exam_date || exam.handling_details?.collection_date) && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CalendarToday sx={{ fontSize: 16, color: 'text.secondary' }} />
                                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                  {new Date(exam.handling_details?.exam_date || exam.handling_details?.collection_date || '').toLocaleDateString('pt-BR')}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>

                        {/* Imagem do exame (anexo) */}
                        {(() => {
                          const objectKey = getFirstImageAttachmentUrl(exam.handling_details?.attachments);
                          return objectKey ? (
                            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<Biotech />}
                                onClick={() => fetchTempAccessAndOpen(objectKey)}
                                disabled={tempAccessLoadingKey === objectKey}
                              >
                                Ver placa de teste
                              </Button>
                              {tempAccessLoadingKey === objectKey && (
                                <CircularProgress size={20} />
                              )}
                              {tempAccessError && tempAccessErrorKey === objectKey && (
                                <Typography variant="caption" color="error">
                                  {tempAccessError}
                                </Typography>
                              )}
                            </Box>
                          ) : null;
                        })()}

                        {/* Resultado principal */}
                        {(exam.handling_details?.exam_result || exam.handling_details?.results?.exam_result || exam.handling_details?.results?.result) && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                              Resultado:
                            </Typography>
                            <Chip 
                              label={exam.handling_details?.exam_result || exam.handling_details?.results?.exam_result || exam.handling_details?.results?.result}
                              color={getSeverityColor(getResultSeverity(exam.handling_details?.exam_result || exam.handling_details?.results?.exam_result || exam.handling_details?.results?.result || ''))}
                              variant="filled"
                              sx={{ fontWeight: 600 }}
                            />
                          </Box>
                        )}

                        {/* Detalhes específicos do teste de mastite com Accordion */}
                        {exam.handling_details?.exam_type === 'mastitis_test' && exam.handling_details?.results && (
                          <Box sx={{ mt: 2 }}>
                            {/* Dropdown 1: Resultados */}
                            <Accordion 
                              sx={{
                                bgcolor: 'grey.50',
                                border: '1px solid',
                                borderColor: 'grey.200',
                                borderRadius: 1,
                                '&:before': {
                                  display: 'none',
                                },
                                boxShadow: 'none',
                              }}
                            >
                              <AccordionSummary
                                expandIcon={<ExpandMore sx={{ color: 'grey.600' }} />}
                                sx={{
                                  bgcolor: 'grey.50',
                                  '& .MuiAccordionSummary-content': {
                                    alignItems: 'center',
                                  },
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                  <LocalHospital sx={{ color: 'grey.600', fontSize: 20 }} />
                                  <Box sx={{ flex: 1 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'grey.600' }}>
                                      Resultado: {exam.handling_details?.results?.exam_result || 'Não informado'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      Clique para ver resultados detalhados
                                    </Typography>
                                  </Box>
                                </Box>
                              </AccordionSummary>
                              
                              <AccordionDetails sx={{ bgcolor: 'background.paper', pt: 2 }}>
                                {/* Sinais do úbere */}
                                {exam.handling_details?.results?.udderSigns && exam.handling_details.results.udderSigns.length > 0 && (
                                  <Box sx={{ mb: 3 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                      Sinais do Úbere:
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                      {exam.handling_details.results.udderSigns.map((sign: string, index: number) => (
                                        <Chip key={index} label={sign} size="small" variant="outlined" color="default" />
                                      ))}
                                    </Box>
                                  </Box>
                                )}

                                {/* Tetos afetados */}
                                {exam.handling_details?.results?.teatsAffected && exam.handling_details.results.teatsAffected.length > 0 && (
                                  <Box sx={{ mb: 3 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                      Tetos Afetados:
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                      {exam.handling_details.results.teatsAffected.map((teat: string, index: number) => (
                                        <Chip key={index} label={teat} size="small" variant="outlined" color="default" />
                                      ))}
                                    </Box>
                                  </Box>
                                )}

                                {/* Aspecto do leite */}
                                {exam.handling_details?.results?.milkAspect && exam.handling_details.results.milkAspect.length > 0 && (
                                  <Box sx={{ mb: 3 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                      Aspecto do Leite:
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                      {exam.handling_details.results.milkAspect.map((aspect: string, index: number) => (
                                        <Chip key={index} label={aspect} size="small" variant="outlined" color="default" />
                                      ))}
                                    </Box>
                                  </Box>
                                )}

                                {/* Resultados microbiológicos detalhados */}
                                {(exam.handling_details.results.staphPrimary || exam.handling_details.results.strepPrimary || exam.handling_details.results.generalPrimary) && (
                                  <Box sx={{ mb: 3 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                      Resultados Microbiológicos:
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                      {exam.handling_details.results.staphPrimary && (
                                        <Typography variant="body2">
                                          • <strong>Staphylococcus (Primário):</strong> {exam.handling_details.results.staphPrimary}
                                        </Typography>
                                      )}
                                      {exam.handling_details.results.staphSecondary && (
                                        <Typography variant="body2">
                                          • <strong>Staphylococcus (Secundário):</strong> {exam.handling_details.results.staphSecondary}
                                        </Typography>
                                      )}
                                      {exam.handling_details.results.strepPrimary && (
                                        <Typography variant="body2">
                                          • <strong>Streptococcus (Primário):</strong> {exam.handling_details.results.strepPrimary}
                                        </Typography>
                                      )}
                                      {exam.handling_details.results.strepSecondary && (
                                        <Typography variant="body2">
                                          • <strong>Streptococcus (Secundário):</strong> {exam.handling_details.results.strepSecondary}
                                        </Typography>
                                      )}
                                      {exam.handling_details.results.generalPrimary && (
                                        <Typography variant="body2">
                                          • <strong>Geral (Primário):</strong> {exam.handling_details.results.generalPrimary}
                                        </Typography>
                                      )}
                                      {exam.handling_details.results.generalSecondary && (
                                        <Typography variant="body2">
                                          • <strong>Geral (Secundário):</strong> {exam.handling_details.results.generalSecondary}
                                        </Typography>
                                      )}
                                    </Box>
                                  </Box>
                                )}

                                {/* Observações */}
                                {exam.handling_details.results.observation && (
                                  <Box sx={{ mb: 3 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                      Observações:
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                                      {exam.handling_details.results.observation}
                                    </Typography>
                                  </Box>
                                )}
                              </AccordionDetails>
                            </Accordion>

                            {/* Dropdown 2: Tratamento Recomendado */}
                            {(exam.handling_details.results.situation || exam.handling_details.results.mainTreatment || exam.handling_details.results.supportiveTreatment || exam.handling_details.results.recommendedAntibiotics || (exam.handling_details.results.aines && exam.handling_details.results.aines.length > 0)) && (
                              <Accordion 
                                sx={{
                                  mt: 1,
                                  bgcolor: 'grey.50',
                                  border: '1px solid',
                                  borderColor: 'grey.300',
                                  borderRadius: 1,
                                  '&:before': {
                                    display: 'none',
                                  },
                                  boxShadow: 'none',
                                }}
                              >
                                <AccordionSummary
                                  expandIcon={<ExpandMore sx={{ color: 'grey.600' }} />}
                                  sx={{
                                    bgcolor: 'grey.50',
                                    '& .MuiAccordionSummary-content': {
                                      alignItems: 'center',
                                    },
                                  }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                    <LocalHospital sx={{ color: 'primary.main', fontSize: 20 }} />
                                    <Box sx={{ flex: 1 }}>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                        Tratamento Recomendado
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        Clique para ver recomendações de tratamento
                                      </Typography>
                                    </Box>
                                  </Box>
                                </AccordionSummary>
                                
                                <AccordionDetails sx={{ bgcolor: 'background.paper', pt: 2 }}>
                                  <Stack spacing={2} sx={{ mb: 3 }}>
                                    {exam.handling_details.results.situation && (
                                      <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                                        <Box component="span" sx={{ fontWeight: 600, color: 'text.primary', mr: 1 }}>
                                          Situação:
                                        </Box>
                                        <Box component="span" sx={{ color: 'text.secondary' }}>
                                          {exam.handling_details.results.situation}
                                        </Box>
                                      </Typography>
                                    )}
                                    {exam.handling_details.results.mainTreatment && (
                                      <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                                        <Box component="span" sx={{ fontWeight: 600, color: 'text.primary', mr: 1 }}>
                                          Tratamento principal:
                                        </Box>
                                        <Box component="span" sx={{ color: 'text.secondary' }}>
                                          {exam.handling_details.results.mainTreatment}
                                        </Box>
                                      </Typography>
                                    )}
                                    {exam.handling_details.results.supportiveTreatment && (
                                      <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                                        <Box component="span" sx={{ fontWeight: 600, color: 'text.primary', mr: 1 }}>
                                          Tratamento de suporte:
                                        </Box>
                                        <Box component="span" sx={{ color: 'text.secondary' }}>
                                          {exam.handling_details.results.supportiveTreatment}
                                        </Box>
                                      </Typography>
                                    )}
                                    {exam.handling_details.results.recommendedAntibiotics && (
                                      <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                                        <Box component="span" sx={{ fontWeight: 600, color: 'text.primary', mr: 1 }}>
                                          Antibióticos recomendados:
                                        </Box>
                                        <Box component="span" sx={{ color: 'text.secondary' }}>
                                          {exam.handling_details.results.recommendedAntibiotics}
                                        </Box>
                                      </Typography>
                                    )}
                                    {exam.handling_details.results.aines && exam.handling_details.results.aines.length > 0 && (
                                      <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
                                          AINES:
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                          {exam.handling_details.results.aines.map((a, idx) => (
                                            <Chip key={`${a}-${idx}`} label={a} size="small" color="primary" variant="outlined" />
                                          ))}
                                        </Box>
                                      </Box>
                                    )}
                                  </Stack>

                                  {/* Aviso de IA movido para o final */}
                                  <Alert
                                    severity="warning"
                                    variant="outlined"
                                    icon={<WarningAmberOutlined />}
                                    role="alert"
                                    aria-live="polite"
                                    sx={{
                                      bgcolor: (theme) => alpha(theme.palette.warning.main, 0.08),
                                      borderColor: 'warning.main',
                                      color: 'warning.dark',
                                      p: 2,
                                    }}
                                  >
                                    <AlertTitle>Atenção</AlertTitle>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      Esta é apenas uma recomendação gerada por IA, e ela pode errar. Consulte um veterinário.
                                    </Typography>
                                  </Alert>
                                </AccordionDetails>
                              </Accordion>
                            )}

                          </Box>
                        )}

                        {/* Datas e informações adicionais */}
                        <Box sx={{ 
                          display: 'flex', 
                          gap: 3, 
                          flexWrap: 'wrap', 
                          pt: 2, 
                          borderTop: '1px solid', 
                          borderColor: 'divider' 
                        }}>
                          {exam.handling_details?.collection_date && (
                            <Box>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                Data da Coleta:
                              </Typography>
                              <Typography variant="body2">
                                {new Date(exam.handling_details.collection_date).toLocaleDateString('pt-BR')}
                              </Typography>
                            </Box>
                          )}
                          {exam.handling_details?.exam_date && (
                            <Box>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                Data do Exame:
                              </Typography>
                              <Typography variant="body2">
                                {new Date(exam.handling_details.exam_date).toLocaleDateString('pt-BR')}
                              </Typography>
                            </Box>
                          )}
                          {exam.handling_details?.plate_id && (
                            <Box>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                ID da Placa:
                              </Typography>
                              <Typography variant="body2">
                                {exam.handling_details.plate_id}
                              </Typography>
                            </Box>
                          )}
                          <Box>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                              ID do Exame:
                            </Typography>
                            <Typography variant="body2">
                              {exam._key}
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    </Box>
                  </Box>
                ))}
              </Box>

              {/* Paginação */}
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={(_, page) => setCurrentPage(page)}
                    color="primary"
                    size="large"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ExamDetail;